import { useCallback, useEffect, useId, useRef, useState } from 'react';

import type { CSSProperties, PointerEvent as ReactPointerEvent, ReactNode } from 'react';

export const SPLIT_STORAGE_KEY = '1c.splitRatio';

// Clamp floors approximate the spec's grid minmax(420px, …) / minmax(480px, …):
// at typical review widths these ratios keep both panes above their min tracks
// while leaving the browser's minmax() as the hard backstop.
const SPLIT_MIN = 0.3;
const SPLIT_MAX = 0.72;
const SPLIT_DEFAULT = 0.58; // 58/42 per spec §5
const COLLAPSE_DEFAULT_PX = 960;

export interface ReviewSplitProps {
  left: ReactNode;
  right: ReactNode;
  storageKey?: string;
  collapseBelowPx?: number;
}

function clampRatio(r: number): number {
  if (Number.isNaN(r)) return SPLIT_DEFAULT;
  return Math.min(SPLIT_MAX, Math.max(SPLIT_MIN, r));
}

function readSavedRatio(key: string): number {
  try {
    const raw = localStorage.getItem(key);
    if (raw == null) return SPLIT_DEFAULT;
    return clampRatio(Number(raw));
  } catch {
    return SPLIT_DEFAULT;
  }
}

export function ReviewSplit({
  left,
  right,
  storageKey = SPLIT_STORAGE_KEY,
  collapseBelowPx = COLLAPSE_DEFAULT_PX,
}: ReviewSplitProps) {
  // gridRef points at the desktop `.review-split` grid: it both carries the
  // `--split` CSS var and supplies the bounding rect for the drag ratio math.
  const gridRef = useRef<HTMLDivElement | null>(null);
  // wrapRef is the element the ResizeObserver measures; in mobile mode it is
  // the collapsed wrapper, in desktop mode it is the grid itself.
  const wrapRef = useRef<HTMLDivElement | null>(null);
  // Lazy initializer reads localStorage once on mount (no ref-during-render).
  const [ratio, setRatio] = useState<number>(() => readSavedRatio(storageKey));
  // ratioRef mirrors the live ratio during a drag so per-frame moves and the
  // commit-on-pointer-up can read it without re-rendering.
  const ratioRef = useRef<number>(ratio);
  const [width, setWidth] = useState<number | null>(null);
  const [mobilePane, setMobilePane] = useState<'doc' | 'data'>('doc');
  const tabsId = useId();

  // Push the initial / committed ratio onto the grid as the --split CSS var.
  useEffect(() => {
    gridRef.current?.style.setProperty('--split', String(ratio));
  }, [ratio]);

  // Measure wrapper width for the responsive branch. ResizeObserver only (no
  // matchMedia) so it tracks the actual container, not the viewport. Re-bind
  // when the rendered branch (and thus wrapRef target) changes.
  const collapsed = width != null && width < collapseBelowPx;
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (typeof w === 'number') setWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [collapsed]);

  const onPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      const grid = gridRef.current;
      if (!grid) return;
      const rect = grid.getBoundingClientRect();
      // Prefer the real layout width; fall back to the ResizeObserver-measured
      // width when layout reports 0 (e.g. jsdom, where getBoundingClientRect
      // has no box model).
      const trackWidth = rect.width > 0 ? rect.width : (width ?? 0);
      const move = (ev: PointerEvent) => {
        if (trackWidth <= 0) return;
        const r = clampRatio((ev.clientX - rect.left) / trackWidth);
        ratioRef.current = r;
        // Per-frame: mutate the CSS var directly, no React re-render.
        grid.style.setProperty('--split', String(r));
      };
      const up = () => {
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
        setRatio(ratioRef.current); // commit to state
        try {
          localStorage.setItem(storageKey, String(ratioRef.current));
        } catch {
          /* storage may be unavailable; ratio still applied in-memory */
        }
      };
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
    },
    [storageKey, width],
  );

  if (collapsed) {
    return (
      <div className="review-split review-split--mobile" ref={wrapRef}>
        <div className="tabs" role="tablist" aria-label="มุมมองเอกสาร">
          <div
            role="tab"
            id={`${tabsId}-doc`}
            aria-selected={mobilePane === 'doc'}
            tabIndex={mobilePane === 'doc' ? 0 : -1}
            className={`tab ${mobilePane === 'doc' ? 'active' : ''}`}
            onClick={() => setMobilePane('doc')}
          >
            เอกสาร
          </div>
          <div
            role="tab"
            id={`${tabsId}-data`}
            aria-selected={mobilePane === 'data'}
            tabIndex={mobilePane === 'data' ? 0 : -1}
            className={`tab ${mobilePane === 'data' ? 'active' : ''}`}
            onClick={() => setMobilePane('data')}
          >
            ข้อมูล
          </div>
        </div>
        <div className="review-pane review-pane--mobile" role="tabpanel">
          {mobilePane === 'doc' ? left : right}
        </div>
      </div>
    );
  }

  // --split is the left-pane fraction; it feeds grid-template-columns in
  // prototype.css. Set inline as well as via the effect so the very first
  // paint already carries the restored/initial ratio.
  const gridStyle = { '--split': String(ratio) } as CSSProperties;

  const setRefs = (node: HTMLDivElement | null) => {
    gridRef.current = node;
    wrapRef.current = node;
  };

  return (
    <div className="review-split" ref={setRefs} style={gridStyle}>
      <div className="review-pane review-pane--doc">{left}</div>
      <div
        className="review-split__handle"
        role="separator"
        aria-orientation="vertical"
        aria-label="ปรับขนาดบานหน้าต่าง"
        onPointerDown={onPointerDown}
      />
      <div className="review-pane review-pane--form">{right}</div>
    </div>
  );
}
